import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_SHAPE_MASK, type ShapeMaskConfig } from "@/lib/meshShapeMask";

export type MeshGradientType = "radial" | "linear" | "conic";
export type MeshBlendMode = "multiply" | "screen" | "normal" | "overlay" | "soft-light" | "plus-lighter" | "lighten" | "darken";
export type MeshMotion = "drift" | "swirl" | "pulse" | "still";
export type MeshRenderer = "css-blobs" | "webgl-mesh";

export type MeshSlot = {
  hex: string;
  enabled: boolean;
  size: number;      // 20-100 (% of viewport)
  x: number;         // -30 to 100
  y: number;         // -30 to 100
  opacity: number;   // 0-1
  // v2 additions
  shape?: MeshGradientType;   // per-slot shape (falls back to global type)
  blend?: MeshBlendMode;      // per-slot blend (falls back to global blend)
  rotation?: number;          // deg, for linear/conic
  aspect?: number;            // width/height ratio, 0.3–3
};

export type MeshGrain = { enabled: boolean; intensity: number }; // 0-0.2

/** A single WebGL mesh anchor — a point in the field that contributes a hue.
 *  x/y are normalized (0–1, where 0.5/0.5 = canvas center).
 *  size is a falloff multiplier (0.2–3): larger = that color dominates a wider area.
 *  opacity is the anchor's weight (0–1) — 0 hides it entirely. */
export type MeshWebglAnchor = {
  hex: string;
  opacity: number;
  x: number;
  y: number;
  size: number;
  enabled: boolean;
};

/** WebGL mesh anchors — always exactly 8 entries (migrator pads). */
export type MeshWebgl = { colors: MeshWebglAnchor[]; speed: number; intensity: number };

export const WEBGL_COLOR_COUNT = 8;

/** Default circular layout for the 8 anchors (matches the original Stripe-style ring). */
export const defaultAnchorPosition = (i: number): { x: number; y: number } => {
  const a = i * (Math.PI / 4);
  return { x: 0.5 + 0.34 * Math.cos(a), y: 0.5 + 0.34 * Math.sin(a) };
};

/** Pad / normalize WebGL anchors to exactly 8 entries. Accepts:
 *  - new shape: MeshWebglAnchor[]
 *  - legacy shape: string[] (just hexes) — pre-anchor-controls configs */
export const padWebglAnchors = (
  arr: readonly (string | Partial<MeshWebglAnchor>)[],
): MeshWebglAnchor[] => {
  const src = arr.length > 0 ? arr : ["#FFFFFF"];
  const out: MeshWebglAnchor[] = [];
  for (let i = 0; i < WEBGL_COLOR_COUNT; i++) {
    const raw = src[i] ?? src[i % src.length] ?? "#FFFFFF";
    const def = defaultAnchorPosition(i);
    if (typeof raw === "string") {
      out.push({ hex: raw, opacity: 1, x: def.x, y: def.y, size: 1, enabled: true });
    } else {
      out.push({
        hex: raw.hex ?? "#FFFFFF",
        opacity: typeof raw.opacity === "number" ? raw.opacity : 1,
        x: typeof raw.x === "number" ? raw.x : def.x,
        y: typeof raw.y === "number" ? raw.y : def.y,
        size: typeof raw.size === "number" ? raw.size : 1,
        enabled: typeof raw.enabled === "boolean" ? raw.enabled : true,
      });
    }
  }
  return out;
};


export type MeshMobileOverride = {
  enabled: boolean;             // master toggle
  baseColor?: string;           // hex; falls back to config.baseColor
  blend?: MeshBlendMode;        // falls back to config.blend
  opacityMultiplier?: number;   // 0–1, multiplied into each slot.opacity
  blurDelta?: number;           // -50..+50 px added to config.blur
  /** Per-slot mobile-only color overrides. Length 8; null/empty = inherit desktop slot.hex. */
  slotHexes?: (string | null)[];
};

export type MeshConfig = {
  version?: 1 | 2;
  slots: MeshSlot[];         // exactly 8 (v2); v1 stored 7 — migrator pads
  type: MeshGradientType;    // global default shape (per-slot can override)
  speed: number;             // 1-10
  blur: number;              // 20-150 px
  blend: MeshBlendMode;      // global default blend
  overlay: { hex: string; opacity: number };
  // v2 additions
  baseColor: string;
  grain: MeshGrain;
  motion: MeshMotion;
  renderer: MeshRenderer;
  webgl: MeshWebgl;
  /** Optional SVG-shape mask layer applied over the renderer. */
  shape?: ShapeMaskConfig;
  /** Optional mobile-only overrides applied when viewport <768px. */
  mobile?: MeshMobileOverride;
};


export type MeshPreset = {
  id: string;
  name: string;
  is_active: boolean;
  config: MeshConfig;
  created_at: string;
  updated_at: string;
};

// ----- Defaults ------------------------------------------------------------

const EMPTY_SLOT = (hex = "#FFFFFF"): MeshSlot => ({
  hex, enabled: false, size: 50, x: 50, y: 50, opacity: 0.5,
  shape: undefined, blend: undefined, rotation: 0, aspect: 1,
});

export const DEFAULT_MESH_CONFIG: MeshConfig = {
  version: 2,
  slots: [
    { hex: "#A8B0E8", enabled: true,  size: 70, x: -15, y: -15, opacity: 0.85, shape: "radial", rotation: 0, aspect: 1 },
    { hex: "#7A85D0", enabled: true,  size: 75, x: 40,  y: 45,  opacity: 0.75, shape: "radial", rotation: 0, aspect: 1 },
    { hex: "#BFD8BF", enabled: true,  size: 60, x: 35,  y: 15,  opacity: 0.80, shape: "radial", rotation: 0, aspect: 1 },
    { hex: "#F0C8C8", enabled: true,  size: 55, x: 10,  y: 50,  opacity: 0.85, shape: "radial", rotation: 0, aspect: 1 },
    { hex: "#D4C5E8", enabled: true,  size: 45, x: 30,  y: 40,  opacity: 0.70, shape: "radial", rotation: 0, aspect: 1 },
    EMPTY_SLOT(),
    EMPTY_SLOT(),
    EMPTY_SLOT(),
  ],
  type: "radial",
  speed: 5,
  blur: 70,
  blend: "multiply",
  overlay: { hex: "#FFFFFF", opacity: 0 },
  baseColor: "#FFFFFF",
  grain: { enabled: false, intensity: 0.04 },
  motion: "drift",
  renderer: "css-blobs",
  webgl: { colors: padWebglAnchors(["#A8B0E8", "#7A85D0", "#BFD8BF", "#F0C8C8", "#D4C5E8", "#FFD1A8", "#A8E0D4", "#C8B8E8"]), speed: 5, intensity: 0.6 },
  shape: { ...DEFAULT_SHAPE_MASK },
};

/** Defensive shape-mask merge for the migrator. */
const mergeShape = (s: Partial<ShapeMaskConfig> | undefined): ShapeMaskConfig => ({
  ...DEFAULT_SHAPE_MASK,
  ...(s ?? {}),
  halo: { ...DEFAULT_SHAPE_MASK.halo, ...((s?.halo ?? {}) as Partial<ShapeMaskConfig["halo"]>) },
  extendOutside: {
    ...DEFAULT_SHAPE_MASK.extendOutside,
    ...((s?.extendOutside ?? {}) as Partial<ShapeMaskConfig["extendOutside"]>),
  },
});


export const speedToDurationSec = (speed: number): number => {
  const clamped = Math.max(1, Math.min(10, speed));
  return Math.round(60 - ((clamped - 1) / 9) * 52);
};

// ----- v1 → v2 migrator (lossless, in-memory) ------------------------------

export const migrateMeshConfig = (raw: unknown): MeshConfig => {
  if (!raw || typeof raw !== "object") return DEFAULT_MESH_CONFIG;
  const c = raw as Partial<MeshConfig> & Record<string, unknown>;
  if (c.version === 2 && Array.isArray(c.slots) && c.slots.length === 8) {
    // Already v2 — fill any missing nested defaults defensively.
    const incomingWebgl = (c.webgl ?? {}) as Partial<MeshWebgl>;
    return {
      ...DEFAULT_MESH_CONFIG,
      ...(c as MeshConfig),
      grain: { ...DEFAULT_MESH_CONFIG.grain, ...(c.grain ?? {}) },
      overlay: { ...DEFAULT_MESH_CONFIG.overlay, ...(c.overlay ?? {}) },
      webgl: {
        ...DEFAULT_MESH_CONFIG.webgl,
        ...incomingWebgl,
        colors: padWebglAnchors(
          (incomingWebgl.colors as Array<string | Partial<MeshWebglAnchor>> | undefined) ??
            DEFAULT_MESH_CONFIG.webgl.colors,
        ),
      },
      slots: (c.slots as MeshSlot[]).map((s) => ({ rotation: 0, aspect: 1, ...s })),
      shape: mergeShape(c.shape as Partial<ShapeMaskConfig> | undefined),
    };
  }
  // v1
  const v1Slots = (Array.isArray(c.slots) ? c.slots : []) as Partial<MeshSlot>[];
  const globalType = (c.type as MeshGradientType) ?? "radial";
  const globalBlend = (c.blend as MeshBlendMode) ?? "multiply";
  const slots: MeshSlot[] = Array.from({ length: 8 }, (_, i) => {
    const s = v1Slots[i];
    if (!s) return EMPTY_SLOT();
    return {
      hex: s.hex ?? "#FFFFFF",
      enabled: s.enabled ?? false,
      size: s.size ?? 50,
      x: s.x ?? 50,
      y: s.y ?? 50,
      opacity: s.opacity ?? 0.5,
      shape: s.shape ?? globalType,
      blend: s.blend ?? globalBlend,
      rotation: s.rotation ?? 0,
      aspect: s.aspect ?? 1,
    };
  });
  const enabledHexes = slots.filter((s) => s.enabled).map((s) => s.hex);
  const webglColors = padWebglAnchors(
    enabledHexes.length > 0 ? enabledHexes : ["#A8B0E8", "#7A85D0", "#BFD8BF", "#F0C8C8"],
  );
  return {
    version: 2,
    slots,
    type: globalType,
    speed: (c.speed as number) ?? 5,
    blur: (c.blur as number) ?? 70,
    blend: globalBlend,
    overlay: { ...DEFAULT_MESH_CONFIG.overlay, ...((c.overlay as MeshConfig["overlay"]) ?? {}) },
    baseColor: (c.baseColor as string) ?? "#FFFFFF",
    grain: { ...DEFAULT_MESH_CONFIG.grain, ...((c.grain as MeshGrain) ?? {}) },
    motion: (c.motion as MeshMotion) ?? "drift",
    renderer: (c.renderer as MeshRenderer) ?? "css-blobs",
    webgl: { colors: webglColors, speed: (c.speed as number) ?? 5, intensity: 0.6 },
    shape: mergeShape(c.shape as Partial<ShapeMaskConfig> | undefined),
  };
};

// ----- Curated starter presets (dropdown only — NOT seeded into DB) --------

export type MeshStarter = { id: string; name: string; description: string; config: MeshConfig };

const starter = (over: Partial<MeshConfig>): MeshConfig => ({
  ...DEFAULT_MESH_CONFIG,
  ...over,
  grain: { ...DEFAULT_MESH_CONFIG.grain, ...(over.grain ?? {}) },
  overlay: { ...DEFAULT_MESH_CONFIG.overlay, ...(over.overlay ?? {}) },
  webgl: { ...DEFAULT_MESH_CONFIG.webgl, ...(over.webgl ?? {}) },
  slots: over.slots ?? DEFAULT_MESH_CONFIG.slots,
});

export const MESH_STARTERS: MeshStarter[] = [
  {
    id: "stripe-classic",
    name: "Stripe Classic",
    description: "WebGL mesh on dark navy — indigo, teal, magenta, peach.",
    config: starter({
      renderer: "webgl-mesh",
      baseColor: "#0A0E27",
      webgl: { colors: padWebglAnchors(["#5B6EE8", "#4ECDC4", "#E94B7C", "#F4A87C", "#7E5BE8", "#3DDC97", "#FFB86B", "#C45BE8"]), speed: 4, intensity: 0.7 },
      grain: { enabled: true, intensity: 0.05 },
    }),
  },
  {
    id: "aurora",
    name: "Aurora",
    description: "WebGL mesh on near-black — green, cyan, violet, pink.",
    config: starter({
      renderer: "webgl-mesh",
      baseColor: "#05060D",
      webgl: { colors: padWebglAnchors(["#3DDC97", "#4ECDC4", "#9B72CF", "#F472B6", "#5BE8C7", "#A0F0E8", "#D496F0", "#F0A6D8"]), speed: 5, intensity: 0.8 },
      grain: { enabled: true, intensity: 0.06 },
    }),
  },
  {
    id: "aurora-heart",
    name: "Aurora Heart",
    description: "Aurora mesh masked to a feathered heart on near-black.",
    config: starter({
      renderer: "webgl-mesh",
      baseColor: "#05060D",
      webgl: { colors: padWebglAnchors(["#3DDC97", "#4ECDC4", "#9B72CF", "#F472B6", "#5BE8C7", "#A0F0E8", "#D496F0", "#F0A6D8"]), speed: 5, intensity: 0.8 },
      grain: { enabled: true, intensity: 0.05 },
      shape: {
        ...DEFAULT_SHAPE_MASK,
        enabled: true,
        mode: "mask",
        preset: "heart",
        size: 75,
        feather: 32,
        halo: { enabled: true, size: 1.45, intensity: 0.45, color: "#000000" },
      },
    }),
  },
  {
    id: "sunset",
    name: "Sunset",
    description: "CSS blobs on warm cream — coral, amber, rose, plum on screen.",
    config: starter({
      renderer: "css-blobs",
      baseColor: "#FFF5EC",
      blend: "screen",
      motion: "drift",
      slots: [
        { hex: "#FF6B6B", enabled: true,  size: 75, x: -10, y: -10, opacity: 0.85, shape: "radial", blend: "screen", rotation: 0, aspect: 1.2 },
        { hex: "#F7931E", enabled: true,  size: 70, x: 45,  y: 10,  opacity: 0.80, shape: "radial", blend: "screen", rotation: 0, aspect: 1 },
        { hex: "#E84393", enabled: true,  size: 65, x: 30,  y: 55,  opacity: 0.75, shape: "radial", blend: "screen", rotation: 0, aspect: 1 },
        { hex: "#6C5CE7", enabled: true,  size: 60, x: 65,  y: 60,  opacity: 0.70, shape: "radial", blend: "screen", rotation: 0, aspect: 0.9 },
        { hex: "#FFD166", enabled: true,  size: 50, x: 10,  y: 60,  opacity: 0.65, shape: "radial", blend: "screen", rotation: 0, aspect: 1 },
        EMPTY_SLOT(), EMPTY_SLOT(), EMPTY_SLOT(),
      ],
    }),
  },
  {
    id: "linear-dark",
    name: "Linear Dark",
    description: "CSS blobs on near-black — indigo and violet on screen, light grain.",
    config: starter({
      renderer: "css-blobs",
      baseColor: "#0A0A0F",
      blend: "screen",
      motion: "drift",
      blur: 90,
      grain: { enabled: true, intensity: 0.05 },
      slots: [
        { hex: "#4F46E5", enabled: true, size: 80, x: -15, y: -10, opacity: 0.9, shape: "radial", blend: "screen", rotation: 0, aspect: 1.1 },
        { hex: "#7C3AED", enabled: true, size: 75, x: 50,  y: 30,  opacity: 0.85, shape: "radial", blend: "screen", rotation: 0, aspect: 1 },
        { hex: "#2563EB", enabled: true, size: 70, x: 20,  y: 60,  opacity: 0.80, shape: "radial", blend: "screen", rotation: 0, aspect: 1 },
        { hex: "#A78BFA", enabled: true, size: 55, x: 65,  y: 65,  opacity: 0.70, shape: "radial", blend: "screen", rotation: 0, aspect: 1 },
        EMPTY_SLOT(), EMPTY_SLOT(), EMPTY_SLOT(), EMPTY_SLOT(),
      ],
    }),
  },
  {
    id: "vercel-soft",
    name: "Vercel Soft",
    description: "Whisper-soft grey-blue on white. No motion. Editorial.",
    config: starter({
      renderer: "css-blobs",
      baseColor: "#FFFFFF",
      blend: "multiply",
      motion: "still",
      blur: 120,
      slots: [
        { hex: "#E0E7FF", enabled: true, size: 80, x: -10, y: -10, opacity: 0.9, shape: "radial", blend: "multiply", rotation: 0, aspect: 1 },
        { hex: "#DBEAFE", enabled: true, size: 75, x: 50,  y: 20,  opacity: 0.85, shape: "radial", blend: "multiply", rotation: 0, aspect: 1 },
        { hex: "#EDE9FE", enabled: true, size: 70, x: 25,  y: 55,  opacity: 0.80, shape: "radial", blend: "multiply", rotation: 0, aspect: 1 },
        { hex: "#F1F5F9", enabled: true, size: 60, x: 65,  y: 60,  opacity: 0.75, shape: "radial", blend: "multiply", rotation: 0, aspect: 1 },
        EMPTY_SLOT(), EMPTY_SLOT(), EMPTY_SLOT(), EMPTY_SLOT(),
      ],
    }),
  },
];

// ----- DB CRUD -------------------------------------------------------------

export const fetchActiveMeshPreset = async (): Promise<MeshConfig | null> => {
  const { data, error } = await supabase
    .from("marketing_mesh_presets")
    .select("config")
    .eq("is_active", true)
    .maybeSingle();
  if (error || !data) return null;
  return migrateMeshConfig(data.config);
};

export const listMeshPresets = async (): Promise<MeshPreset[]> => {
  const { data, error } = await supabase
    .from("marketing_mesh_presets")
    .select("*")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return ((data ?? []) as unknown as MeshPreset[]).map((p) => ({ ...p, config: migrateMeshConfig(p.config) }));
};

export const createMeshPreset = async (name: string, config: MeshConfig): Promise<MeshPreset> => {
  const { data, error } = await supabase
    .from("marketing_mesh_presets")
    .insert({ name, config: config as never, is_active: false })
    .select("*")
    .single();
  if (error) throw error;
  return { ...(data as unknown as MeshPreset), config: migrateMeshConfig((data as { config: unknown }).config) };
};

export const updateMeshPreset = async (
  id: string,
  patch: { name?: string; config?: MeshConfig },
): Promise<void> => {
  const payload: { name?: string; config?: MeshConfig } = {};
  if (patch.name !== undefined) payload.name = patch.name;
  if (patch.config !== undefined) payload.config = patch.config;
  const { error } = await supabase
    .from("marketing_mesh_presets")
    .update(payload as never)
    .eq("id", id);
  if (error) throw error;
};

export const deleteMeshPreset = async (id: string): Promise<void> => {
  const { error } = await supabase.from("marketing_mesh_presets").delete().eq("id", id);
  if (error) throw error;
};

export const activateMeshPreset = async (id: string): Promise<void> => {
  const { error } = await supabase.rpc("activate_marketing_mesh_preset", { _preset_id: id });
  if (error) throw error;
};

// ----- Admin-local preview override ---------------------------------------

const PREVIEW_KEY = "mkt-mesh-preview-config";
const PREVIEW_CHANNEL = "mkt-mesh-preview";
export const PREVIEW_EVENT = "mkt-mesh-preview-change";

type PreviewMessage =
  | { kind: "update"; config: MeshConfig | null }
  | { kind: "request" };

/** Lazy BroadcastChannel wrapper. Falls back to a no-op object when the API
 *  isn't available (older Safari, SSR). */
let _channel: BroadcastChannel | null | undefined; // undefined = uninit, null = unsupported
const getChannel = (): BroadcastChannel | null => {
  if (_channel !== undefined) return _channel;
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    _channel = null;
    return null;
  }
  try {
    _channel = new BroadcastChannel(PREVIEW_CHANNEL);
  } catch {
    _channel = null;
  }
  return _channel;
};

export const meshPreviewBus = {
  publish(config: MeshConfig | null): void {
    const ch = getChannel();
    if (!ch) return;
    try {
      ch.postMessage({ kind: "update", config } satisfies PreviewMessage);
    } catch {
      /* ignore */
    }
  },
  /** Ask any other tabs with preview enabled to re-publish their current config. */
  request(): void {
    const ch = getChannel();
    if (!ch) return;
    try {
      ch.postMessage({ kind: "request" } satisfies PreviewMessage);
    } catch {
      /* ignore */
    }
  },
  subscribe(handler: (msg: PreviewMessage) => void): () => void {
    const ch = getChannel();
    if (!ch) return () => {};
    const listener = (e: MessageEvent<PreviewMessage>) => {
      if (e.data && typeof e.data === "object" && "kind" in e.data) handler(e.data);
    };
    ch.addEventListener("message", listener);
    return () => ch.removeEventListener("message", listener);
  },
};

/** Write/clear the local preview override. Returns true on success.
 *  Also publishes over BroadcastChannel so other tabs (incl. just-opened ones
 *  that send a `request`) reflect the change live. */
export const setPreviewMeshConfig = (config: MeshConfig | null): boolean => {
  if (typeof window === "undefined") return false;
  try {
    if (config === null) localStorage.removeItem(PREVIEW_KEY);
    else localStorage.setItem(PREVIEW_KEY, JSON.stringify(config));
    window.dispatchEvent(new Event(PREVIEW_EVENT));
    meshPreviewBus.publish(config);
    return true;
  } catch {
    // QuotaExceededError (large embedded SVG), private mode, etc.
    return false;
  }
};
export const getPreviewMeshConfig = (): MeshConfig | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(PREVIEW_KEY);
    if (!raw) return null;
    return migrateMeshConfig(JSON.parse(raw));
  } catch {
    return null;
  }
};

// ----- URL-hash preview transport (cross-origin safe) ---------------------

const HASH_KEY = "mesh";
const HASH_VERSION = "v2";
/** Cap encoded length to keep URLs sane and avoid browser URL limits.
 *  ~6 KB easily covers normal configs; embedded large SVGs may exceed it. */
export const MAX_HASH_PREVIEW_BYTES = 6000;

const toBase64Url = (s: string): string => {
  const b64 = typeof btoa !== "undefined"
    ? btoa(unescape(encodeURIComponent(s)))
    : Buffer.from(s, "utf-8").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};
const fromBase64Url = (s: string): string => {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
  const raw = typeof atob !== "undefined"
    ? atob(b64)
    : Buffer.from(b64, "base64").toString("binary");
  try {
    return decodeURIComponent(escape(raw));
  } catch {
    return raw;
  }
};

/** Encode a config into a URL-hash fragment payload (without leading `#`).
 *  Returns null if the encoded form exceeds MAX_HASH_PREVIEW_BYTES. */
export const encodePreviewToHash = (config: MeshConfig): string | null => {
  try {
    const payload = toBase64Url(JSON.stringify(config));
    if (payload.length > MAX_HASH_PREVIEW_BYTES) return null;
    return `${HASH_KEY}=${HASH_VERSION}:${payload}`;
  } catch {
    return null;
  }
};

/** Decode a config from `window.location.hash`, if any. */
export const decodePreviewFromHash = (): MeshConfig | null => {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash;
  if (!raw) return null;
  const hash = raw.startsWith("#") ? raw.slice(1) : raw;
  // Support either `#mesh=v2:...` or `#x&mesh=v2:...`
  const parts = hash.split("&");
  for (const part of parts) {
    const [k, v] = part.split("=");
    if (k !== HASH_KEY || !v) continue;
    const [ver, payload] = v.includes(":") ? v.split(":") : ["v1", v];
    if (ver !== HASH_VERSION || !payload) continue;
    try {
      return migrateMeshConfig(JSON.parse(fromBase64Url(payload)));
    } catch {
      return null;
    }
  }
  return null;
};
